import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component, CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  Inject,
  LOCALE_ID,
  OnInit,
  PLATFORM_ID,
  QueryList, ViewChild,
  ViewChildren
} from '@angular/core';
import {ChatSocketService} from '../../services/chat-socket.service';
import { AsyncPipe, isPlatformBrowser, NgClass } from '@angular/common';
import {TextFieldModule} from '@angular/cdk/text-field';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {TdjComponent} from "../tdj/tdj.component";
import {EndChatComponent} from "../end-chat/end-chat.component";
import {LimitPopupComponent} from "../limit-popup/limit-popup.component";
import {BehaviorSubject} from "rxjs";
import { ActivatedRoute, RouterLink } from '@angular/router';
import {TrackingService} from '../../services/tracking.service';
import {LimitPopupService} from '../../services/limit-popup.service';

@Component({
    selector: 'app-chat',
    imports: [
    AsyncPipe,
    NgClass,
    TextFieldModule,
    ReactiveFormsModule,
    TdjComponent,
    EndChatComponent,
    LimitPopupComponent,
    RouterLink
],
    templateUrl: './chat.component.html',
    styleUrl: './chat.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ChatComponent implements OnInit, AfterViewInit {
  @ViewChildren('messages') messages?: QueryList<ElementRef>;
  @ViewChild('container') container?: ElementRef;
  discussionClosed$ = new BehaviorSubject(false);
  messages$ = this.chatSocket.messages$;
  typing$ = this.chatSocket.typing$;
  form = new FormGroup({
    message: new FormControl({value: '', disabled: true}, Validators.required),
  });
  hasPsychics$ = new BehaviorSubject(false);
  externalCards = new BehaviorSubject<[number, number, number] | undefined>(undefined);
  debug$ = new BehaviorSubject(false);

  constructor(@Inject(PLATFORM_ID) private platformId: Object,
              @Inject(LOCALE_ID) private localeId: string,
              private route: ActivatedRoute,
              private chatSocket: ChatSocketService,
              private trackingService: TrackingService,
              public limitPopupService: LimitPopupService) {

  }

  /** Heure (HH:MM) d'un message à partir de son timestamp (ms ou s epoch, ou date ISO). */
  formatTime(timestamp: any): string {
    if (timestamp === null || timestamp === undefined || timestamp === '') return '';
    let ms = typeof timestamp === 'number' ? timestamp : Number(timestamp);
    if (isNaN(ms)) {
      const parsed = Date.parse(timestamp);
      if (isNaN(parsed)) return '';
      ms = parsed;
    }
    // epoch en secondes -> millisecondes
    if (ms < 1e12) ms = ms * 1000;
    const date = new Date(ms);
    if (isNaN(date.getTime())) return '';
    const locale = this.localeId === 'fr-x-source' ? 'fr-FR' : this.localeId;
    try {
      return new Intl.DateTimeFormat(locale, {hour: '2-digit', minute: '2-digit'}).format(date);
    } catch {
      return new Intl.DateTimeFormat('fr-FR', {hour: '2-digit', minute: '2-digit'}).format(date);
    }
  }

  ngOnInit() {
    this.route.queryParamMap.subscribe(p => {
      this.debug$.next(!!(p.get('dev') && p.get('dev') === '1'));
    })
    if (isPlatformBrowser(this.platformId)) {
      // this.chatSocket.connect()
      //   .subscribe(() => {
      //     this.form.enable();
      //   });
      this.chatSocket.createSocket();
      this.form.enable();
    }
    this.messages$.subscribe(messages => {
      const cardsMessage = messages.find(m => m.type === 'cards');
      if (cardsMessage) {
        this.externalCards.next(cardsMessage.content as [number, number, number]);
      }
      this.hasPsychics$.next(!!messages.find(m => m.experts && m.experts.length > 0));
      if (messages.find(m => m.special_action === 'discussion_closed')) {
        this.discussionClosed$.next(true);
        this.chatSocket.close();
      } else {
        this.discussionClosed$.next(false);
      }

      // Vérifier si la limite est atteinte
      if (messages.find(m => m.special_action === 'limit_reached')) {
        this.trackingService.gameEvent('ShowLimitReached');
        this.discussionClosed$.next(true);
        this.limitPopupService.showLimitPopup();
      }
    })
  }

  ngAfterViewInit() {
    this.messages?.changes.subscribe(() => {
      setTimeout(() => {
        const scrollHeight = this.container?.nativeElement.scrollHeight;
        const offsetHeight = this.container?.nativeElement.offsetHeight;
        this.container?.nativeElement.scrollTo({
          top: scrollHeight - offsetHeight,
          left: 0,
          behavior: "smooth",
        });
      }, 60);
    });
    this.typing$.subscribe(typing => {
      if (!typing) return;
      setTimeout(() => {
        const scrollHeight = this.container?.nativeElement.scrollHeight;
        const offsetHeight = this.container?.nativeElement.offsetHeight;
        this.container?.nativeElement.scrollTo({
          top: scrollHeight - offsetHeight,
          left: 0,
          behavior: "smooth",
        });
      }, 60);
    });
  }

  sendMessage($event: Event) {
    $event.preventDefault();
    if (this.form.invalid) {
      return;
    }
    this.chatSocket.sendMessage(this.form.get('message')!.value!);
    this.form.get('message')!.reset();
  }

  sendCards(cards: [number, number, number]) {
    this.chatSocket.sendCards(cards);
    this.form.get('message')!.reset();
    this.trackingService.gameEvent("TarotDuJourDrawing", {cards: cards});
  }

  reset() {
    this.chatSocket.removeUid();
    this.chatSocket.close();
    this.chatSocket.messages$.next([]);
    this.chatSocket.entities$.next({});
    this.externalCards.next(undefined);
    this.hasPsychics$.next(false);
    this.form.disable();

    setTimeout(() => {
      if (isPlatformBrowser(this.platformId)) {
        console.log('RECONNECT ?');
        this.chatSocket.createSocket();
        this.form.enable();
      }
    }, 2000);
  }
}
